<template>
  <v-dialog
    v-model="localDialog"
    max-width="850px"
    :retain-focus="false"
    :persistent="false"
    @click:outside="handleOutsideClick"
    @after-leave="handleAfterLeave"
  >
    <v-card v-if="localDialog && !isLoading && loadError" class="expanded-article-card">
      <v-card-title class="article-header">
        <SvgIcon v-once :path="mdiBookOpenPageVariant" size="28" class="book-icon" />
        <span class="article-title">Article unavailable</span>
        <v-spacer></v-spacer>
        <v-btn icon class="close-btn" @click="handleClose">
          <SvgIcon v-once :path="mdiClose" size="22" class="close-icon" />
        </v-btn>
      </v-card-title>
      <v-card-text class="article-content">
        <p>{{ loadErrorMessage }}</p>
      </v-card-text>
    </v-card>
    <v-card
      v-else-if="localDialog && !isLoading && localArticle && localArticle.content_json"
      :key="['card', localArticle.id, localArticle.latest_history_id || localArticle.current_history_id || 'no-latest'].join(':')"
      class="expanded-article-card"
    >
      <!-- Header -->
      <v-card-title class="article-header">
        <SvgIcon v-once :path="mdiBookOpenPageVariant" size="28" class="book-icon" />
        <span class="article-title" :title="localArticle.title">{{ localArticle.title }}</span>
        <v-spacer></v-spacer>
        
        <v-btn icon class="close-btn" @click="handleClose">
          <SvgIcon v-once :path="mdiClose" size="22" class="close-icon" />
        </v-btn>
      </v-card-title>

      <!-- Article Content -->
      <v-card-text class="article-content" @click="interceptMention" ref="content">
        <ArticleContent
          v-if="localArticle && localArticle.content_json"
          :key="[
            'ac',
            localArticle.id,
            localArticle.latest_history_id || localArticle.current_history_id || 'no-latest',
            localArticle.last_read_history_id || localArticle.last_read_id || 'no-lastread'
          ].join(':')"
          :latestJson="localArticle.content_json"
          :lastReadJson="localArticle.last_read_json || null"
          :latestHistoryId="localArticle.latest_history_id || localArticle.current_history_id"
          :lastReadHistoryId="localArticle.last_read_history_id || localArticle.last_read_id || null"
          :diffHtml="localArticle.diff_html || ''"
          :latestHtml="localArticle.content_html || ''"
          :openTick="openTick"
        />
      </v-card-text>

      <!-- Pinned Action Bar -->
      <v-card-actions class="action-buttons">
        <div v-if="isManager" class="left-buttons">
          <v-btn small color="primary" @click="$emit('edit', localArticle.id)">
            <SvgIcon :path="mdiPencil" size="18" left />
            <span class="action-text">Edit</span>
          </v-btn>
          <v-btn small color="error" @click="$emit('delete', localArticle.id)">
            <SvgIcon :path="mdiDelete" size="18" left />
            <span class="action-text">Delete</span>
          </v-btn>
        </div>
        <v-spacer></v-spacer>
        <template v-if="localArticle.ack_status !== 'read'">
          <v-tooltip>
            <template #activator="{ props }">
              <SvgIcon
                v-bind="props"
                :path="iconForStatus(localArticle.ack_status).path"
                size="20"
                :class="['inline-status', iconForStatus(localArticle.ack_status).css]"
              />
            </template>
            <span>{{ iconForStatus(localArticle.ack_status).tooltip }}</span>
          </v-tooltip>

          <v-btn tonal small color="primary" @click="ackSection(localArticle)">
            Mark as read
          </v-btn>
        </template>
        <template v-else>
          <v-tooltip>
            <template #activator="{ props }">
              <SvgIcon
                v-bind="props"
                :path="iconForStatus('read').path"
                size="24"
                :class="['status-icon', iconForStatus('read').css]"
              />
            </template>
            <span>{{ iconForStatus('read').tooltip }}</span>
          </v-tooltip>
        </template>
        <v-spacer></v-spacer>
        <v-btn small text class="back-button" @click="$emit('pop-one')">
          <SvgIcon :path="mdiArrowLeft" size="18" left />
          <!-- we always render the span, but hide it on small -->
          <span class="back-text">
            <template v-if="chainLength > 1">
              Back to {{ prevTitle }}
            </template>
            <template v-else>
              Back to Handbook
            </template>
          </span>
        </v-btn>
      </v-card-actions>
      </v-card>
      <v-card v-else class="expanded-article-card">
        <v-card-title class="article-header">
          <v-skeleton-loader type="heading" width="60%"></v-skeleton-loader>
          <v-spacer></v-spacer>
          <v-skeleton-loader type="chip"></v-skeleton-loader>
        </v-card-title>
        <v-card-text class="article-content">
          <v-skeleton-loader type="paragraph, paragraph, paragraph"></v-skeleton-loader>
        </v-card-text>
        <v-card-actions class="action-buttons">
          <v-skeleton-loader type="button"></v-skeleton-loader>
          <v-spacer></v-spacer>
          <v-skeleton-loader type="button"></v-skeleton-loader>
        </v-card-actions>
      </v-card>
  </v-dialog>
</template>

<script>
import { mdiBookOpenPageVariant, mdiClose, mdiPencil, mdiDelete, mdiArrowLeft } from "@mdi/js";
import { iconForStatus } from "@/utils/ackStatus";
import api from '@/plugins/axios';
import { getSectionMaybeCached, putSection } from '@/utils/handbookCache';
import ArticleContent from '@/tiptap/components/ArticleContent.vue'

export default {
  name: "HandbookArticleModal",
  components: {
    ArticleContent,
  },
  props: {
    showDialog: Boolean,
    article: {
      type: Object,
      default: () => ({}),
    },
    chainLength: {
      type: Number,
      default: 1,
    },
    prevTitle: {
      type: String,
      default: "",
    },
    isManager: Boolean,
  },
  data() {
    return {
      localDialog: false,
      localArticle: {},
      mdiBookOpenPageVariant,
      mdiClose,
      mdiPencil,
      mdiDelete,
      mdiArrowLeft,
      isLoading: false,
      loadError: false,
      loadErrorMessage: '',
      openTick: 0,
    };
  },
  watch: {
    showDialog: {
      immediate: true,
      async handler(open) {
        if (!open) {
          this.localDialog = false;
          this.localArticle = {};
          this.isLoading = false;
          this.loadError = false;
          this.loadErrorMessage = '';
          return;
        }
        // 1) enter loading state and clear stale article
        this.localDialog = true;
        this.isLoading = true;
        this.loadError = false;
        this.loadErrorMessage = '';
        this.localArticle = {};

        try {
          const { data } = await api.get(
            `/api/handbook-sections/${this.article.id}/`,
            { headers: { 'Cache-Control': 'no-cache' } }
          );
          putSection(data);
          this.localArticle = data;
        } catch (e) {
          const cached = getSectionMaybeCached(this.article.id);
          if (process.env.NODE_ENV !== 'production') console.debug('detail fetch (open) failed', e);
          if (cached) {
            this.localArticle = cached;
          } else {
            this.loadError = true;
            this.loadErrorMessage = e?.response?.status === 404
              ? 'This article no longer exists.'
              : 'Could not load the article. Please try again.';
          }
        }

        this.isLoading = false;
        await this.$nextTick();
        this.openTick++;
        this.resetScroll();
      }
    },
    article: {
      immediate: false,
      async handler(newArticle) {
        if (!this.localDialog || !newArticle || !newArticle.id) return;

        this.isLoading = true;
        this.loadError = false;
        this.loadErrorMessage = '';
        this.localArticle = {};

        try {
          const { data } = await api.get(
            `/api/handbook-sections/${newArticle.id}/`,
            { headers: { 'Cache-Control': 'no-cache' } }
          );
          putSection(data);
          this.localArticle = data;
        } catch (e) {
          const cached = getSectionMaybeCached(newArticle.id);
          if (process.env.NODE_ENV !== 'production') console.debug('detail fetch (change) failed', e);
          if (cached) {
            this.localArticle = cached;
          } else {
            this.loadError = true;
            this.loadErrorMessage = e?.response?.status === 404
              ? 'This article no longer exists.'
              : 'Could not load the article. Please try again.';
          }
        }

        this.isLoading = false;
        await this.$nextTick();
        this.resetScroll();
      },
    },
  },
  methods: {
    iconForStatus,
    handleOutsideClick() {
      this.localDialog = false;
    },
    handleClose() {
      this.localDialog = false;
    },
    handleAfterLeave() {
      this.$emit("close-all");
      this.localArticle = {};
      this.highlightedContent = '';
      this.isLoading = false;
      this.loadError = false;
      this.loadErrorMessage = '';
    },
    interceptMention(e) {
      const link = e.target.closest("a");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      if (!href.includes('/handbook/article/')) return;
      e.preventDefault();
      let newId = null;
      try {
        const url = new URL(href, window.location.origin);
        const segments = url.pathname.split("/").filter(Boolean);
        const idx = segments.indexOf("article");
        if (idx !== -1) newId = segments[idx + 1];
      } catch (_e) { /* ignore parse errors */ }
      if (!newId) return;
      this.$emit("mention-link-clicked", { newId });
    },
    async ackSection(article) {
      if (!window.confirm("By clicking Yes, you acknowledge that you have read and understood this article.")) {
        return;
      }
      try {
        await api.post(
          `/api/handbook-sections/${article.id}/acknowledge/`,
          {}
        );
        try {
          // Pull the fresh canonical representation immediately so diff/read
          // state updates in-place without reopening the modal.
          const { data } = await api.get(
            `/api/handbook-sections/${article.id}/`,
            { headers: { 'Cache-Control': 'no-cache' } }
          );
          putSection(data);
          this.localArticle = data;
          this.stabilizeContentScroll();
        } catch (refreshErr) {
          // If the follow-up read fails, keep the UI responsive.
          this.localArticle = {
            ...this.localArticle,
            ack_status: "read",
          };
          this.stabilizeContentScroll();
          if (process.env.NODE_ENV !== 'production') {
            console.debug('ack follow-up refresh failed', refreshErr);
          }
        }

        // notify dashboard immediately that this section was acknowledged
        this.$emit("acknowledged", article.id, this.localArticle);
      } catch (err) {
        console.error('Error acknowledging section:', err);
      }
    },
    // Removed: openDiff, prefetchChainLinks, enhanceContentDom, idlePrefetchNextLinks, silentAck
    /**
     * Ensure the article starts scrolled to the top.
     */
    resetScroll() {
      this.$nextTick(() => {
        const wrapper = this.$refs.content && this.$refs.content.parentElement;
        if (wrapper) wrapper.scrollTop = 0;
      });
    },
    stabilizeContentScroll() {
      this.$nextTick(() => {
        const wrapper = this.$refs.content && this.$refs.content.parentElement;
        if (!wrapper) return;
        const clamp = () => {
          const maxScroll = Math.max(0, wrapper.scrollHeight - wrapper.clientHeight);
          if (wrapper.scrollTop > maxScroll) {
            wrapper.scrollTop = maxScroll;
          }
        };
        clamp();
        requestAnimationFrame(clamp);
        setTimeout(clamp, 60);
      });
    },
  },
  mounted() {
    if (this.showDialog) {
      this.localDialog = true;
    }
  },
};
</script>

<style scoped>
.expanded-article-card {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  max-height: calc(100vh - 40px);
}

/* Header bar */
.article-header {
  background: #3f51b5;
  color: white;
  /* combined padding: top/bottom 13px, left/right 12px */
  padding: 13px 12px;
  display: flex;
  align-items: center;
}

/* Truncate long titles in the modal header (especially on phones) */
.article-header .article-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* allow flexbox to shrink the title below its content width */
  min-width: 0;
  flex: 0 1 auto;
}

/* Header icons */
.book-icon {
  font-size: 28px !important;
  margin-right: 12px !important;
  transform: translate(-0.1mm, -0.1mm) !important;
}
.close-btn {
  background: white !important;
  color: black !important;
  width: 32px !important;
  height: 32px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.2);
}
.close-icon {
  font-size: 22px !important;
  color: black !important;
}
.close-btn:hover {
  background: #f0f0f0 !important;
}

/* Content area */
.article-content {
  padding: 20px;
  line-height: 1.4;
  text-align: justify;
  flex: 1 1 auto;
  overflow-y: auto;
}
/* Allow horizontal scroll if a table is too wide */
.article-content { overflow-x: auto; }

/* Action bar */
.action-buttons {
  display: flex;
  justify-content: space-between;
  padding: 12px 15px;
  border-top: 1px solid #ddd;
}
.left-buttons {
  display: flex;
  gap: 10px;
}

/* Shrink manager buttons on small screens */
@media (max-width: 600px) {
  .left-buttons .v-btn {
    min-width: auto;
    padding: 4px 8px;
    font-size: 0.75rem;
  }
  .left-buttons .action-text,
  .back-text {
    display: none;
  }
  .back-button {
    min-width: auto;
    padding: 4px 8px;
    font-size: 0.75rem;
  }
}


.status-icon {
  margin-right: 8px;
  color: #4caf50 !important;
}

.inline-status {
  margin-right: 0px;
}

</style>
